// Renders a Tier ≥ 1 proposed action (or Tier 4 plan) as a
// Confirm / Cancel card, plus a phrase input field for Tier 3.
// Emits the required inputs (confirmations[] + explicitConfirmations)
// to the parent when Confirm is clicked; parent posts them to
// /api/agent/chat.
//
// Load-bearing detail: the required-phrase text for Tier 3 is
// declared on the capability itself and surfaced via the
// tier3-phrase-block event ONLY AFTER a phrase-less confirmation is
// blocked. Pre-first-confirm, we don't know the phrase — the LLM's
// assistant message states it in prose, and we render an input
// labelled "phrase" without a placeholder value.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, HStack, Input, Text, VStack, Badge, Divider, Code } from '@chakra-ui/react';
import type { PendingAction, ToolResult } from './types';
import { estimateExceedsCap } from '../spend/cap';
import { planDraftsEnabled, requestPlanDraft, type DraftQuote } from '../marketingPlans/drafts';

type Props = {
  action:   PendingAction;
  onConfirm: (toolCallId: string, phrase?: string) => void;
  onCancel:  (toolCallId: string) => void;
  busy?:    boolean;
  spendCapUsd?: number | null;
  /** Brand the chat card was asked to quote. Image Studio can leave this on the previous brand. */
  brandId?: string | null;
  /** Brand a confirm request will send. Comes from the active brand, not the stale chat prop. */
  activeBrandId?: string | null;
};

type QuoteState = 'idle' | 'loading' | 'ready' | 'missing' | 'mismatch' | 'closed';

function sameBrand(brandId: string | null, activeBrandId: string | null): boolean {
  if (!brandId) return false;
  if (!activeBrandId) return true;
  return brandId === activeBrandId;
}

/** marketing.draftPlan, including the wire name marketing__draftPlan. */
export function isDraftPlanTool(name: string): boolean {
  const dotted = name.replace(/__/g, '.');
  return dotted === 'draftPlan' || dotted.endsWith('marketing.draftPlan');
}

export function planEstimateUsd(plan: ToolResult | undefined): number | null {
  if (!plan || !plan.ok) return null;
  const amount = plan.data.estimateUsd;
  return typeof amount === 'number' && Number.isFinite(amount) && amount >= 0 ? amount : null;
}

export function ConfirmationCard({
  action, onConfirm, onCancel, busy, spendCapUsd = null, brandId = null, activeBrandId = null
}: Props) {
  const [phrase, setPhrase] = useState('');
  const needsPhrase = action.tier === 3 || !!action.requiredPhrase;
  const estimateUsd = planEstimateUsd(action.plan);
  const overCap = estimateExceedsCap(estimateUsd, spendCapUsd);
  const draftTool = isDraftPlanTool(action.toolName);
  const draftsOn = draftTool && planDraftsEnabled();
  const aligned = sameBrand(brandId, activeBrandId);
  const [quote, setQuote] = useState<DraftQuote | null>(null);
  const [quotedFor, setQuotedFor] = useState<string | null>(null);
  const [quoteState, setQuoteState] = useState<QuoteState>(() => {
    if (!draftTool) return 'idle';
    if (!planDraftsEnabled()) return 'closed';
    if (!sameBrand(brandId, activeBrandId)) return 'mismatch';
    return brandId ? 'loading' : 'missing';
  });
  const activeRef = useRef(activeBrandId || brandId);
  activeRef.current = activeBrandId || brandId;
  const quoteBrandRef = useRef(brandId);
  quoteBrandRef.current = brandId;

  useEffect(() => {
    if (!draftTool) return;
    if (!draftsOn) {
      setQuote(null);
      setQuotedFor(null);
      setQuoteState('closed');
      return;
    }
    if (!brandId || !aligned) {
      setQuote(null);
      setQuotedFor(null);
      setQuoteState(brandId ? 'mismatch' : 'missing');
      return;
    }
    const forBrand = brandId;
    let cancelled = false;
    setQuote(null);
    setQuotedFor(null);
    setQuoteState('loading');
    void requestPlanDraft(forBrand, false).then((result) => {
      if (cancelled || quoteBrandRef.current !== forBrand || activeRef.current !== forBrand) return;
      setQuotedFor(forBrand);
      setQuote(result.quote);
      setQuoteState(result.quote ? 'ready' : 'missing');
    });
    return () => { cancelled = true; };
  }, [draftTool, draftsOn, brandId, aligned]);

  const border = borderColorFor(action.tier);
  const bg     = bgColorFor(action.tier);

  const argsPretty = useMemo(() => {
    try { return JSON.stringify(action.args, null, 2); }
    catch { return String(action.args); }
  }, [action.args]);

  const quoteForActive = quotedFor != null && quotedFor === brandId && (activeBrandId == null || quotedFor === activeBrandId);
  const draftBlocked = draftTool && (
    !draftsOn
    || !aligned
    || !quoteForActive
    || quoteState !== 'ready'
    || !quote
    || quote.overCap
    || quote.closed != null
    || Boolean(quote.blockedReason)
    || !quote.estimate
    || !quote.dailyCap
  );
  const canSubmit = !busy && !overCap && !draftBlocked && (!needsPhrase || phrase.trim().length > 0);

  return (
    <Box p={3} borderWidth="2px" borderColor={border} bg={bg} borderRadius="md">
      <HStack justify="space-between" mb={2}>
        <VStack align="flex-start" spacing={0}>
          <Text fontSize="11px" color="brand.muted" textTransform="uppercase" letterSpacing="0.5px">
            {action.plan ? 'Proposed workflow' : 'Proposed action'}
          </Text>
          <Text fontSize="14px" fontWeight="700">{humaniseToolName(action.toolName)}</Text>
        </VStack>
        <Badge>tier {action.tier}</Badge>
      </HStack>

      {action.plan && action.plan.ok && <PlanPreview plan={action.plan} />}

      {draftTool && (
        <Box mb={2} data-testid="draft-estimate">
          {quoteState === 'closed' ? (
            <Text fontSize="12px" data-testid="draft-blocked">Plan drafts aren't turned on.</Text>
          ) : quoteState === 'mismatch' ? (
            <Text fontSize="12px" data-testid="draft-brand-mismatch">Estimate Not available</Text>
          ) : quoteState === 'loading' || quoteState === 'idle' ? (
            <Text fontSize="12px">Checking the estimate…</Text>
          ) : (
            <>
              <Text fontSize="12px">Estimate {quote?.estimate ?? 'Not available'}</Text>
              <Text fontSize="12px">Daily cap {quote?.dailyCap ?? 'Not available'}</Text>
              <Text fontSize="12px">Spent today {quote?.spentToday ?? 'Not available'}</Text>
              {quote?.blockedReason && <Text fontSize="12px" data-testid="draft-blocked">{quote.blockedReason}</Text>}
            </>
          )}
        </Box>
      )}

      {!action.plan && (
        <>
          <Text fontSize="11px" color="brand.muted" mb={1}>Arguments</Text>
          <Code display="block" whiteSpace="pre-wrap" fontSize="11px" p={2} maxH="180px" overflowY="auto">
            {argsPretty}
          </Code>
        </>
      )}

      {overCap && estimateUsd != null && spendCapUsd != null && (
        <Text fontSize="12px" color="orange.700" mt={2} data-testid="confirm-over-cap">
          Estimate ${estimateUsd.toFixed(2)} is above the ${spendCapUsd.toFixed(2)} cap.
        </Text>
      )}

      {needsPhrase && (
        <>
          <Divider my={3} />
          <Text fontSize="11px" color="brand.muted" mb={1}>
            {action.requiredPhrase
              ? `Type "${action.requiredPhrase}" to confirm.`
              : 'This is a hard-to-reverse action. Type the phrase the assistant asked for.'}
          </Text>
          <Input
            size="sm"
            placeholder={action.requiredPhrase || 'e.g. PUBLISH TO META'}
            value={phrase}
            onChange={(e) => setPhrase(e.target.value)}
            fontFamily="mono"
            isDisabled={busy}
          />
        </>
      )}

      <HStack justify="flex-end" spacing={2} mt={3}>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onCancel(action.toolCallId)}
          isDisabled={busy}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          colorScheme={action.tier >= 3 ? 'orange' : action.tier >= 2 ? 'blue' : 'green'}
          onClick={() => {
            if (draftTool && !quoteForActive) return;
            onConfirm(action.toolCallId, needsPhrase ? phrase.trim() : undefined);
          }}
          isDisabled={!canSubmit}
        >
          {action.plan ? 'Run plan' : 'Confirm'}
        </Button>
      </HStack>
    </Box>
  );
}

function PlanPreview({ plan }: { plan: ToolResult }) {
  if (!plan.ok) return null;
  const d = plan.data as any;
  return (
    <VStack align="stretch" spacing={2} mb={2}>
      <Text fontSize="13px">{d.summary}</Text>
      <HStack spacing={4} fontSize="12px" color="brand.muted" flexWrap="wrap">
        <Text><strong>{d.totalSteps}</strong> steps</Text>
        {d.estimateUsd != null && <Text><strong>${Number(d.estimateUsd).toFixed(2)}</strong> estimated</Text>}
        {d.estimateWallMs != null && <Text>~{Math.round(d.estimateWallMs / 60000)} min</Text>}
        {d.concurrency != null && <Text>concurrency {d.concurrency}</Text>}
        {d.reversible === false && <Badge fontSize="10px" colorScheme="orange">not reversible</Badge>}
      </HStack>
      {Array.isArray(d.sampleSteps) && d.sampleSteps.length > 0 && (
        <VStack align="stretch" spacing={0} borderWidth="1px" borderColor="brand.border" borderRadius="sm" p={2} bg="white" maxH="160px" overflowY="auto">
          {d.sampleSteps.slice(0, 10).map((s: any, i: number) => (
            <Text key={i} fontSize="11px" noOfLines={1}>• {s.productName || s.productId || JSON.stringify(s)}</Text>
          ))}
          {d.totalSteps > d.sampleSteps.length && (
            <Text fontSize="11px" color="brand.muted" mt={1}>… and {d.totalSteps - d.sampleSteps.length} more</Text>
          )}
        </VStack>
      )}
      {d.note && <Text fontSize="11px" color="brand.muted">{d.note}</Text>}
    </VStack>
  );
}

function borderColorFor(tier: number): string {
  if (tier >= 3) return 'orange.400';
  if (tier >= 2) return 'blue.400';
  return 'green.400';
}
function bgColorFor(tier: number): string {
  if (tier >= 3) return 'orange.50';
  if (tier >= 2) return 'blue.50';
  return 'green.50';
}
function humaniseToolName(mangled: string): string {
  // 'catalog__listProducts' → 'catalog.listProducts'
  return mangled.replace(/__/g, '.');
}
